import React from 'react'

import { Section, SectionHeading, Button } from '../../../../components/ui'
import { chatEntryProps } from '../../../../components/whatsapp-chat'
import { bookingPath, defaultWashSlug, enterBooking } from '../../../../lib/booking-entry'

import './who-turns-up.css'

// SLOT 7.6 - WHO TURNS UP. Design direction §7.6, rebuilt 4 Oct against the
// hero that now exists.
//
// The objection, restated. §7.6 was written when nothing else on the page
// named a person, so it put Barry Cohen here with his photograph and his
// number, and the question it answered was "is there anyone in charge?".
// The hero answers that now: he stands at the foot of the fold as a named
// head of operations (sections/hero/index.js, `.hero-barry`). Running him
// again here would put the same man and the same photograph on the page
// twice, three screens apart, and would still leave the harder question
// unanswered.
//
// So this section answers the question the fold cannot: not "is there a
// boss" but "who is the person who will be standing on my street next to my
// car, and what does this company actually know about him?". That is a
// question about records, not about personalities, and it is answered by
// facts rather than by a face. Barry is not mentioned, photographed or
// alluded to below. Neither is "you don't need to be there".
//
// THE FOLD NOW CLAIMS THIS SECTION'S SUBJECT OUT LOUD. HeroHelper's trust
// line reads "Insured, vetted washers" (app/helpers/hero_helper.rb,
// `hero_trust_items`). That is three words of assertion on the first screen,
// and this is the section that has to be worth them: it is where "vetted"
// stops being an adjective and becomes a list of the things actually held on
// file, each one with a column or a code path behind it. A visitor who
// believed the fold does not need this section. A visitor who did not is the
// one this section is for.
//
// WHY THERE IS NO PHOTOGRAPH, which is a finding and not a shortcut.
//
// The direction asked for "a real photograph of Barry in daylight next to a
// car" and instructed, in the same sentence, that the section runs text only
// at 640px until such a photograph exists. It still does not exist, and the
// CORRECTION of 4 Oct in the direction says why nothing substitutes for it:
// of the photography we own, exactly one frame (gallery-image-01) shows an
// operator, the van and the work together. The hero leads on it. §7.4 uses
// the rest. The stock in gs://gowashmycar-photos/assets/ is bought,
// landscape, and graded teal and orange against this palette. There is
// therefore no owned photograph of a washer that the page has not already
// used, and a bought photograph of a stranger would make this section's one
// claim, that we know who turns up, a lie in its own illustration.
//
// A well set record of what is on file beats that, and 4 Oct made the record
// stronger in two ways that are worth more than a face would have been.
//
//   THE SHAPE CHANGED. It was a lead column beside a narrow ruled list, which
//   is the shape of an FAQ. It is now the page's own heading block over a
//   full width ruled sheet, term on the left and what we hold on the right,
//   with every rule running the whole 1152px container. That is the shape of
//   a job sheet or an insurance schedule, which is the paperwork a trade
//   actually keeps, and at 1440 the six terms now read straight down one
//   edge instead of being set as six headlines inside a column.
//
//   IT IS THE SAME SHAPE AS §7.4, ON PURPOSE. Both sections are now a
//   `<SectionHeading>` and one artefact: there it is a filmstrip of
//   photographs, here it is a record. They are the two halves of one
//   argument, photographs of the work and a record of who does it, and the
//   two cross-reference each other in copy: §7.4's closing note sends the
//   reader here for the person who took the photographs, and the line under
//   this record sends them back for what that person produced. A term in
//   this record is set in the same face and weight as the wash name under a
//   photograph up there (`.rw-frame-lead` / `.who-term`), so a reader who has
//   scanned one already knows how to scan the other.
//
// EVERY CLAIM BELOW, AND WHERE IT COMES FROM. Nothing here is written from
// memory of how the business works; each line was read out of the code that
// runs it.
//
//   "works for themselves"      app/views/pages/terms_of_use.html.erb:75:
//                               "not an employment service and does not
//                               serve as an employer of any User". Operators
//                               are not staff, and `Agent#company_name`
//                               exists because some of them trade as a
//                               company. Saying "our team" here would be
//                               false, so the section says what is true and
//                               then says what holds it together.
//   approved by hand            NEW TODAY, AND THE REASON IT IS NOW THE
//                               FIRST ROW. `Agent.is_approved` defaults to
//                               false and only an administrator pressing
//                               Approve on the "New" list flips it
//                               (app/admin/agent.rb, `member_action
//                               :approve`); operator sign-up itself is open
//                               to anyone (`devise_for :agents`,
//                               `:registerable`). Until 4 Oct that flag was
//                               checked when BUILDING a job feed
//                               (app/queries/operator_jobs_query.rb) and in
//                               `assignable_agents`, but not in
//                               Order::AcceptService, which is the one path
//                               that actually dispatches somebody to a
//                               customer's home: it checked that a caller
//                               was signed in and nothing else, and order
//                               ids are sequential. It now refuses both the
//                               actor and the assignee unless
//                               `is_active? && is_approved?`
//                               (Order::AcceptService#approved?,
//                               NOT_APPROVED), with three specs on it in
//                               spec/services/order/accept_service_spec.rb:
//                               an operator nobody approved, one who has
//                               been switched off, and an admin trying to
//                               assign a job to an unapproved account. The
//                               sentence "a new account cannot take a job
//                               at all" was a half truth yesterday and is
//                               enforced today, which is exactly why it is
//                               stated plainly and first. It said "cannot
//                               accept a job" until 5 Oct; the gate is the
//                               same, but "accept" is our word for it and no
//                               customer copy uses it any more.
//   a message on the day        REWRITTEN 5 Oct. This row used to promise
//                               that "the confirmation email gives you their
//                               first and last name", sourced to the "Your
//                               operator" row in app/views/user_mailer/
//                               order_accepted.html.haml. That email has not
//                               been sent to a customer since 2026-09-29:
//                               OrdersEvents#on_accept fires Slack and
//                               nothing else, and the WhatsApp twin is
//                               marked "UNUSED since 2026-09-29" in
//                               config/whatsapp_templates.yml. The booking
//                               confirmation that IS sent (order_booked) and
//                               its WhatsApp twin (order_booked_v2) carry no
//                               name and cannot: they go out before anyone
//                               has the job. So the claim was false in every
//                               branch, not merely branch-dependent.
//                               What is true, and is what the row says now,
//                               is the pair of messages on the day:
//                               order_on_my_way ("your GoWashMyCar washer is
//                               on the way... about 30 minutes") and
//                               order_arrived ("outside now"), fired by
//                               OrdersEvents#on_my_way / #on_arrived. §7.2
//                               states the same pair, from the same source.
//                               THERE IS NOWHERE A CUSTOMER IS GIVEN A
//                               WASHER'S NAME. Do not put one back here
//                               without a template that prints it.
//   their own van and kit       Trade fact, not a convenience feature, and
//                               that is why it is worded as equipment the
//                               washer owns rather than as a service we
//                               provide. app/helpers/seo_helper.rb FAQ: "Our
//                               operators arrive in fully equipped vans with
//                               their own water and power, so we only need to
//                               park within about 10 metres of the car." The
//                               fold states the same fact as a customer
//                               benefit ("Our own water and power",
//                               hero_helper.rb) and §7.2 states it as a step;
//                               here it is the answer to "what does this
//                               person turn up with", which is a different
//                               question, and it carries the kerb clause
//                               because that is the one thing the customer's
//                               street has to supply.
//   insurance, with a date      `agents.insurance_valid_until` is a date
//                               column on the operator record, and
//                               app/admin/agent.rb has it as both an index
//                               column and a filter, which is what "the
//                               office can see who is close to it" means.
//                               Deliberately NOT "checked before every job":
//                               no code enforces that, and claiming it would
//                               be inventing a credential. The fold says
//                               "Insured"; this is the level under it.
//   only the areas they cover   app/queries/operator_jobs_query.rb:
//                               `unsorted_available_orders` scopes a
//                               non-admin operator's job feed to
//                               `service_area_id` in the areas joined
//                               through `AgentsServiceArea`. A washer
//                               literally cannot see a booking outside them.
//   a rating that follows them  app/models/feedback.rb: rating 1 to 5,
//                               `belongs_to :agent`, unique per user per
//                               order ("rating applicable once per order").
//                               `Agent#rating` averages that operator's
//                               feedbacks and jobs_controller.rb returns it
//                               to the operator's own dashboard, which is
//                               why the last clause can say it sits on their
//                               screen. No number is printed here:
//                               seo_helper.rb explains at length why this
//                               site does not publish its own score, and
//                               §7.5 owns the published one.
//   the washer took the photos  app/services/order/complete_service.rb. The
//                               completion call will not run at all unless
//                               `@order.agent_id == @agent.id`, and the photo
//                               URLs it stores must sit under
//                               /uploads/order/image/<this order id>/ in our
//                               own bucket or they are dropped and flagged.
//                               The photographs and the `completed: true`
//                               are written in the same call, which is why
//                               the line says "in the same moment the wash
//                               was marked done" rather than "afterwards".
//
// THE FACTS BELOW WERE CONFIRMED BY ROBIN ON 5 OCT 2026, and they replace
// the old rationale here ("no training course, no years of experience"),
// which held while nothing was confirmed: every valeter has at least five
// years' experience; every one is trained by Barry Cohen (head of
// operations, valeting in London since 1999) before they start; every one is
// ID checked, right-to-work checked and referenced; every one is covered by
// public liability insurance (no amount and no specific-damage promise is
// stated); and every wash is done by hand with professional products and
// microfibre. Nothing beyond those facts may be added here. 25 Oct 2026
// (Barry, via Robin): the water type and pH-neutral are no longer claimed;
// the kit row said "deionised water for a spot-free finish" until then.
// 2 NOV 2026: NO METHOD CLAIMS AT ALL. Robin: "we're not doing two bucket
// method". So no bucket counts, no grit guards, no microfibre specifics and
// no drying technique anywhere a customer reads, and clay bar only as the
// paid extra it is. The row said "and fresh microfibre on every car" until
// today, and on 2 Nov it stopped naming kit at all: it is "Professional
// valeters" now, and says that valeting is their trade. What sells the work instead is the outcome and the proof:
// the photographs of the finished car, the wash count, the valeter.

// NO PHONE CALL. Robin, 26 Oct 2026: "dont think we should offer to call the
// phone number on the website" and "whatsapp and chat and email enough imo".
// This line said "Call or WhatsApp us" with a tel: link until then; the way
// to a person is now the same chat entry as the floating chat button
// (chatEntryProps: the website chat Aider answers, or WhatsApp on a phone
// that chose it, docs/CHATWOOT.md, Website chat). docs/DECISIONS.md, 26 Oct.

const RECORD = [
  {
    term: 'Five years minimum',
    held: 'Every valeter has at least five years in the trade. Your car is never anyone’s first.'
  },
  {
    term: 'Trained by Barry',
    held:
      'Barry Cohen has been valeting cars in London since 1999. Every valeter trains with him and works to his standard.'
  },
  {
    term: 'Vetted',
    held: 'ID checked, right to work confirmed and references taken up.'
  },
  {
    term: 'Fully insured',
    held: 'Public liability insurance on every valeter, every job.'
  },
  {
    term: 'Professional valeters',
    held: 'Valeting is their trade, and every car is washed by hand from start to finish.'
  },
  {
    term: 'Self-sufficient',
    held: 'A fully equipped van with its own water and power. Nothing from your house, ever.'
  }
]

export const WhoTurnsUp = () => (
  <Section className="who" background="stone">
    <SectionHeading intro="Every valeter has at least five years’ experience, is trained by Barry and is vetted before they touch your car.">
      Who we send to your car
    </SectionHeading>

    {/* A <dl>: six terms and what stands behind each one. The rules are the
        design; six cards would be six more boxes on a page that already
        carries wash cards and photo tiles. */}
    <dl className="who-record">
      {RECORD.map(row => (
        <div className="who-row" key={row.term}>
          <dt className="who-term">{row.term}</dt>
          <dd className="who-held">{row.held}</dd>
        </div>
      ))}
    </dl>

    <div className="who-foot">
      <p className="who-after">Every photo on this page was taken by the valeter who did the job.</p>
      {/* NOT "Want to talk to a person?" (2 Nov 2026, the register pass).
          A rhetorical question, and "a person" only needs saying on a site
          that expects you to suspect a bot. DESIGN.md §8.12. */}
      <p className="who-contact">We are here if you would rather talk it through, on chat or on WhatsApp.</p>
      <a className="gwmc-link who-chat" {...chatEntryProps('Hi GoWashMyCar! I have a question about a wash')}>
        Start a chat
      </a>
      <Button
        className="who-book"
        variant="primary"
        href={bookingPath(defaultWashSlug())}
        onClick={event => {
          event.preventDefault()
          enterBooking(defaultWashSlug(), 'who_turns_up')
        }}>
        Book a professional valeter
      </Button>
    </div>
  </Section>
)
